import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import clsx from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useCustomOptionsStyles } from './CustomOptions.style';

const RadioOption = ({ option, controlKey, onControlChange }) => {
  const handleClick = () => onControlChange(controlKey, option.value);

  return (
    <FormControlLabel
      key={`${controlKey}-${option.value}`}
      value={option.value}
      control={<Radio size='small' />}
      label={option.label}
      onClick={handleClick}
    />
  );
};

const CheckboxOption = ({ item, isChecked, onControlChange, context }) => {
  const handleClick = () => onControlChange(item.key);

  return (
    <FormControlLabel
      key={`autoCompleteWithControl-control-item-${context}-${item.name}`}
      onClick={handleClick}
      control={<Checkbox size='small' checked={isChecked} />}
      label={item.label}
    />
  );
};

export const CustomOptions = ({
  setBottomOffset,
  control,
  controlClass,
  context,
  note,
  dataFromQuery,
}) => {
  const classes = useCustomOptionsStyles();
  const customOptionsRef = useRef();
  const [tooltipOpen, setTooltipOpen] = useState({});

  useEffect(() => {
    if (customOptionsRef?.current) {
      setBottomOffset(customOptionsRef.current.getBoundingClientRect()?.height || 0);
    }
  }, [setBottomOffset]);

  const onClick = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const onControlChange = useCallback(
    (key, value) => {
      if (control?.onChange) {
        const newValue = value !== undefined ? value : !control.controlValues[key];
        control.onChange(newValue, key);
      }
    },
    [control?.onChange, control?.controlValues],
  );

  const isNoteHidden = note?.hideCallback?.(dataFromQuery);

  return (
    <div ref={customOptionsRef} className={classes.customOptionWrapper}>
      {control?.controlItems && control?.controlValues && (
        <FormControl onClick={onClick} className={clsx(classes.controlClass, controlClass)}>
          {control.controlItems.map(c => {
            if (c.type === 'radio' && c.options) {
              return (
                <fieldset key={`radio-options-${context}-${c.name}`} className={classes.fieldset}>
                  <Typography variant='body2' component='div' sx={{ fontWeight: 'bold', mt: 1 }}>
                    {c.label.toUpperCase()}
                  </Typography>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <RadioGroup aria-label={c.name} value={control.controlValues[c.key]}>
                      <div className={classes.controlGroup}>
                        {c.options.map(option => (
                          <RadioOption
                            key={option.value}
                            option={option}
                            controlKey={c.key}
                            onControlChange={onControlChange}
                          />
                        ))}
                      </div>
                    </RadioGroup>
                    {c.tooltip && (
                      <Tooltip
                        title={<div style={{ whiteSpace: 'pre-line' }}>{c.tooltip}</div>}
                        open={tooltipOpen[c.key] || false}
                        placement='bottom-start'
                        disableHoverListener
                        disableFocusListener
                        disableTouchListener
                      >
                        <IconButton
                          size='small'
                          sx={{ ml: 1, p: 0.5 }}
                          onClick={e => {
                            e.preventDefault();
                            e.stopPropagation();
                            setTooltipOpen(prev => ({
                              ...prev,
                              [c.key]: !prev[c.key],
                            }));
                          }}
                        >
                          <InfoOutlinedIcon fontSize='small' />
                        </IconButton>
                      </Tooltip>
                    )}
                  </div>
                </fieldset>
              );
            }
            return (
              <fieldset key={`checkbox-options-${context}-${c.name}`} className={classes.fieldset}>
                <CheckboxOption
                  key={c.name}
                  item={c}
                  isChecked={!!control.controlValues[c.key]}
                  onControlChange={onControlChange}
                  context={context}
                />
              </fieldset>
            );
          })}
        </FormControl>
      )}
      {note && (
        <Typography
          onClick={onClick}
          className={clsx(classes.noteLabel, {
            [classes.noteLabelHidden]: isNoteHidden,
          })}
        >
          {note.label}
        </Typography>
      )}
    </div>
  );
};
